import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { ScrollView, StyleSheet, Text, TouchableOpacity, View } from "react-native";
import { observer } from "mobx-react";
import { useTranslation } from "react-i18next";
import { Ionicons } from "@expo/vector-icons";
import { arrayMove } from "@dnd-kit/sortable";

import Colors from "../../../../Resources/Color";
import Fonts from "../../../../Resources/Fonts";
import GlobalStyles from "../../../../Resources/Styles";
import CustomAlert from "../../../Components/CustomAlert";
import { useDevice, useScreenDimensions } from "../../../_layout";
import { useToast } from "../../../contexts/ToastContext";
import { useMainViewScroll } from "../../../platform/mainViewScrollContext";
import NutritionEditorActionsMenu from "../../shared/NutritionEditorActionsMenu";
import { buildNutritionEditorActionItems } from "../../shared/nutritionEditorActions";
import AddFoodModal from "../../foods/editor/AddFoodModal";
import { COACH_NUTRITION_BOOTSTRAP_RESOURCES } from "../../coachNutritionBootstrap";
import { useNutritionDragSensors } from "../../shared/useNutritionDragSensors";
import {
  buildCanonicalFoodFilters,
  FOOD_SEARCH_SURFACE_PICKER,
} from "../../foods/foodSearchService";
import { useCanonicalFoodSearch } from "../../foods/useCanonicalFoodSearch";
import NutritionDraftStatusPill from "../../shared/NutritionDraftStatusPill";
import NutritionSyncNotice from "../../shared/NutritionSyncNotice";
import { RecipeEditorDetailsSection } from "./RecipeEditorDetailsSection";
import { RecipeIngredientsSection } from "./RecipeIngredientsSection";
import { RecipeEditorSummarySection } from "./RecipeEditorSummarySection";
import { NutritionEntitySyncStatus } from "../../../Firestore/nutrition/OptimisticNutritionEntity";
import { UI_MACRO_DISPLAY_ORDER } from "../../shared/nutritionFormatters";
import { getNutritionEntityDisplayName } from "../../shared/nutritionEntityNameUtils";
import {
  addIngredientToRecipeForm,
  addEquivalentToRecipeForm,
  INGREDIENT_DATABASE_OPTIONS,
  INGREDIENT_SORT_OPTIONS,
  MICRONUTRIENT_FIELDS,
  applyAutoCalculatedEquivalentState,
  buildIngredientFromSelectedFood,
  buildLinkedEquivalentFromReference,
  getFoodStableKey,
  getRecipeDerivedClassification,
  getIngredientQuantityInGrams,
  getIngredientSelectedUnitKey,
  getIngredientTotals,
  getMacroCalorieBreakdown,
  getRecipeTotals,
  getRecipeDraftValidity,
  isRecipeDraftSemanticallyEmpty,
  isFallbackIngredient,
  syncIngredientAutoCalculatedEquivalences,
  updateIngredientQuantityState,
  updateIngredientUnitState,
} from "../recipeLogic";
import {
  getNutritionLimitMessageDescriptor,
  getNutritionLimitMessageVariables,
  shouldShowNutritionLimitWarning,
  validateRecipeDraftLimits,
} from "../../nutritionLimits";
import {
  buildCanonicalPickerResults,
  matchesPickerBrowserFilter,
  PICKER_FILTER_ALL,
  PICKER_FILTER_CATALOG,
  PICKER_FILTER_CREATED_BY_ME,
  PICKER_FILTER_EQUIVALENTS,
} from "../../shared/foodPicker/pickerBrowser";
import { useFoodPickerRevealInMainView } from "../../shared/foodPicker/foodPickerReveal";

const SectionCard = ({ icon, title, children, style }) => (
  <View style={[styles.sectionCard, style]}>
    <View style={GlobalStyles.CardHeader}>
      <View style={GlobalStyles.CardHeaderMain}>
        <View style={[GlobalStyles.CardIcon, styles.sectionIconWrap]}>
          <Ionicons name={icon} size={14} color={Colors.grayColor} />
        </View>
        <Text style={[GlobalStyles.CardTitle, styles.sectionTitleUppercase]}>
          {title}
        </Text>
      </View>
    </View>
    {children}
  </View>
);

export const RecipeEditorView = observer(function RecipeEditorView({
  coach,
  recipe,
  isEditing,
  onBack,
  onDuplicate,
  onDelete,
}) {
  const { t, i18n } = useTranslation();
  const { showToast } = useToast();
  const { revealInMainView } = useMainViewScroll();
  const { screenSize } = useScreenDimensions();
  const { isMobile } = useDevice();
  const language = i18n?.resolvedLanguage || i18n?.language || "en";
  const searchInputRef = useRef(null);
  const ingredientSearchInputPressingRef = useRef(false);
  const mainPickerPanelRootRef = useRef(null);
  const equivalencePickerPanelRootRef = useRef(null);
  const ingredientSearchTextRef = useRef("");
  const [ingredientSearchText, setIngredientSearchText] = useState("");
  ingredientSearchTextRef.current = ingredientSearchText;
  const [ingredientSortKey, setIngredientSortKey] = useState("default");
  const [ingredientPickerFilterKey, setIngredientPickerFilterKey] =
    useState(PICKER_FILTER_ALL);
  const [equivalencePickerDatabaseKey, setEquivalencePickerDatabaseKey] =
    useState(PICKER_FILTER_ALL);
  const [equivalenceTargetId, setEquivalenceTargetId] = useState(null);
  const [isIngredientBrowserOpen, setIsIngredientBrowserOpen] = useState(false);
  const [isAddFoodModalVisible, setIsAddFoodModalVisible] = useState(false);
  const [microsExpanded, setMicrosExpanded] = useState(false);
  const [isBackButtonHovered, setIsBackButtonHovered] = useState(false);
  const [isDeleteAlertVisible, setIsDeleteAlertVisible] = useState(false);
  const [recipeEntity, setRecipeEntity] = useState(() =>
    typeof recipe?.applyDraft === "function" ? recipe : null,
  );

  const selectIngredientSearchInputText = useCallback(() => {
    const selectionEnd = String(ingredientSearchTextRef.current ?? "").length;

    if (selectionEnd === 0) {
      return;
    }

    const applySelection = () => {
      const input = searchInputRef.current;

      if (!input) {
        return;
      }

      input.setSelectionRange?.(0, selectionEnd);
      input.select?.();
      input.setNativeProps?.({
        selection: { start: 0, end: selectionEnd },
      });
    };

    if (typeof requestAnimationFrame === "function") {
      requestAnimationFrame(applySelection);
      return;
    }

    setTimeout(applySelection, 0);
  }, []);

  const focusIngredientSearchInput = useCallback(
    ({ selectText = false, ignorePressGuard = false } = {}) => {
      if (ingredientSearchInputPressingRef.current && !ignorePressGuard) {
        return;
      }

      searchInputRef.current?.focus?.();

      if (selectText) {
        selectIngredientSearchInputText();
      }
    },
    [selectIngredientSearchInputText],
  );

  useEffect(() => {
    if (typeof recipe?.applyDraft === "function") {
      setRecipeEntity(recipe);
      return;
    }

    if (!recipe?.id || typeof coach?.nutritionStore?.upsertRecipeEntity !== "function") {
      setRecipeEntity(null);
      return;
    }

    setRecipeEntity(coach.nutritionStore.upsertRecipeEntity(recipe));
  }, [coach, recipe]);

  useEffect(() => {
    setIngredientSearchText("");
    setIngredientSortKey("default");
    setIngredientPickerFilterKey(PICKER_FILTER_ALL);
    setEquivalencePickerDatabaseKey(PICKER_FILTER_ALL);
    setEquivalenceTargetId(null);
    setIsIngredientBrowserOpen(false);
    setIsAddFoodModalVisible(false);
    setMicrosExpanded(false);
  }, [recipeEntity?.id, t]);

  useEffect(() => {
    coach?.ensureNutritionResourcesFromCloud?.(
      COACH_NUTRITION_BOOTSTRAP_RESOURCES.recipeEditor,
    )?.catch?.(() => {});
  }, [coach]);

  const form = recipeEntity?.draft ?? {
    name: "",
    description: "",
    imageUri: null,
    typeKey: "main-string",
    preparationTime: "",
    ingredients: [],
  };

  const recipeTotals = useMemo(
    () => getRecipeTotals(form.ingredients),
    [form.ingredients],
  );
  const draftSaveState = useMemo(() => getRecipeDraftValidity(form), [form]);
  const ingredientsForSave = draftSaveState.ingredientsForSave;
  const recipeClassification = useMemo(
    () => getRecipeDerivedClassification(ingredientsForSave),
    [ingredientsForSave],
  );
  const macroBreakdown = useMemo(
    () => getMacroCalorieBreakdown(recipeTotals),
    [recipeTotals],
  );
  const hasFallbackIngredients = useMemo(
    () => draftSaveState.hasFallbackIngredients,
    [draftSaveState.hasFallbackIngredients],
  );
  const hasInvalidIngredientQuantities = draftSaveState.hasInvalidIngredientQuantities;
  const saveValidationMessage = hasFallbackIngredients
    ? t("replaceLegacyIngredientsBeforeSavingRecipe-string")
    : hasInvalidIngredientQuantities
      ? t("ingredientQuantityRequired-string")
      : draftSaveState?.limitsValidation?.ok === false
        ? t(
            getNutritionLimitMessageDescriptor(draftSaveState.limitsValidation)?.description,
            getNutritionLimitMessageVariables(draftSaveState.limitsValidation),
          )
      : null;
  const applyFormUpdate = useCallback(
    (nextFormOrUpdater) => {
      const nextForm =
        typeof nextFormOrUpdater === "function"
          ? nextFormOrUpdater(recipeEntity.draft)
          : nextFormOrUpdater;

      recipeEntity.applyDraft(nextForm);
    },
    [recipeEntity],
  );
  const coachFoods = Array.isArray(coach?.nutritionStore?.sortedFoods)
    ? coach.nutritionStore.sortedFoods
    : [];
  const equivalents = Array.isArray(coach?.nutritionStore?.sortedEquivalents)
    ? coach.nutritionStore.sortedEquivalents
    : [];
  const ingredientSortOptions = useMemo(
    () =>
      INGREDIENT_SORT_OPTIONS.map((option) => ({
        ...option,
        label: t(option.labelKey),
      })),
    [t],
  );
  const inlineFilterOptions = useMemo(
    () =>
      INGREDIENT_DATABASE_OPTIONS.map((option) => ({
        ...option,
        label: t(option.labelKey),
      })),
    [t],
  );
  const mainFilterOptions = useMemo(
    () => [
      {
        key: PICKER_FILTER_ALL,
        label: t("all-string"),
      },
      {
        key: PICKER_FILTER_CREATED_BY_ME,
        label: t("createdByMe-string"),
      },
      {
        key: PICKER_FILTER_CATALOG,
        label: t("catalogSourceFoodDatabase-string", "Food database"),
      },
      {
        key: PICKER_FILTER_EQUIVALENTS,
        label: t("equivalents-string"),
      },
    ],
    [t],
  );
  const selectedSortOption =
    ingredientSortOptions.find((option) => option.key === ingredientSortKey) ||
    ingredientSortOptions[0];
  const selectedMainFilterOption =
    mainFilterOptions.find((option) => option.key === ingredientPickerFilterKey) ||
    mainFilterOptions[0];
  const selectedInlineFilterOption =
    inlineFilterOptions.find((option) => option.key === equivalencePickerDatabaseKey) ||
    inlineFilterOptions[0];
  const inlineFoodFilters = useMemo(() => {
    if (equivalencePickerDatabaseKey === "createdByMe") {
      return buildCanonicalFoodFilters({ source: "coach" });
    }

    if (equivalencePickerDatabaseKey === "catalog") {
      return buildCanonicalFoodFilters({ source: "catalog" });
    }

    return buildCanonicalFoodFilters();
  }, [equivalencePickerDatabaseKey]);
  const mainFoodFilters = useMemo(() => {
    if (ingredientPickerFilterKey === PICKER_FILTER_CREATED_BY_ME) {
      return buildCanonicalFoodFilters({ source: "coach" });
    }

    if (ingredientPickerFilterKey === PICKER_FILTER_CATALOG) {
      return buildCanonicalFoodFilters({ source: "catalog" });
    }

    return buildCanonicalFoodFilters();
  }, [ingredientPickerFilterKey]);
  const inlineFoodSearch = useCanonicalFoodSearch({
    coachFoods,
    query: ingredientSearchText,
    filters: inlineFoodFilters,
    sortField: selectedSortOption?.field,
    pageSize: 8,
    surface: FOOD_SEARCH_SURFACE_PICKER,
    language,
  });
  const mainFoodSearch = useCanonicalFoodSearch({
    coachFoods,
    query: ingredientSearchText,
    filters: mainFoodFilters,
    sortField: selectedSortOption?.field,
    pageSize: 8,
    surface: FOOD_SEARCH_SURFACE_PICKER,
    language,
  });
  const equivalenceTargetIngredient =
    form.ingredients.find((ingredient) => ingredient.id === equivalenceTargetId) ||
    null;
  useFoodPickerRevealInMainView({
    isVisible: isIngredientBrowserOpen && !equivalenceTargetIngredient,
    panelRootRef: mainPickerPanelRootRef,
    revealInMainView,
    focusSearchInput: focusIngredientSearchInput,
  });
  useFoodPickerRevealInMainView({
    isVisible: Boolean(equivalenceTargetIngredient),
    panelRootRef: equivalencePickerPanelRootRef,
    revealInMainView,
    focusSearchInput: focusIngredientSearchInput,
  });
  const isCompactLayout = screenSize === "small";
  const useStackedIngredientLayout = screenSize !== "large";
  const hasEditorActions = !isRecipeDraftSemanticallyEmpty(form);
  const topBarMenuItems = useMemo(
    () =>
      buildNutritionEditorActionItems({
        t,
        includeDuplicate: hasEditorActions && typeof onDuplicate === "function",
        includeDelete: hasEditorActions && typeof onDelete === "function",
      }),
    [hasEditorActions, onDelete, onDuplicate, t],
  );
  const macronutrientsTitle = language?.startsWith("es")
    ? "Macronutrientes"
    : "Macronutrients";
  const summaryMacroItems = useMemo(
    () => {
      const macroItems = {
        protein: {
          key: "protein",
          label: t("protein-string"),
          grams: Number(recipeTotals.protein ?? 0),
          share:
            macroBreakdown.totalCalories > 0
              ? (macroBreakdown.proteinCalories / macroBreakdown.totalCalories) * 100
              : 0,
          color: Colors.proteinColor,
        },
        carbs: {
          key: "carbs",
          label: t("carbs-string"),
          grams: Number(recipeTotals.carbs ?? 0),
          share:
            macroBreakdown.totalCalories > 0
              ? (macroBreakdown.carbsCalories / macroBreakdown.totalCalories) * 100
              : 0,
          color: Colors.carbsColor,
        },
        fats: {
          key: "fats",
          label: t("fats-string"),
          grams: Number(recipeTotals.fats ?? 0),
          share:
            macroBreakdown.totalCalories > 0
              ? (macroBreakdown.fatsCalories / macroBreakdown.totalCalories) * 100
              : 0,
          color: Colors.fatsColor,
        },
      };

      return UI_MACRO_DISPLAY_ORDER.map((key) => macroItems[key]);
    },
    [macroBreakdown, recipeTotals, t],
  );
  const micronutrientSummaryItems = useMemo(
    () =>
      MICRONUTRIENT_FIELDS.map((field) => ({
        ...field,
        label: t(field.labelKey),
        value: Number(recipeTotals.micros?.[field.key] ?? 0),
      })),
    [recipeTotals.micros, t],
  );
  const ingredientResults = inlineFoodSearch.items;
  const mainIngredientResults = useMemo(() => {
    return buildCanonicalPickerResults(
      {
        foods: mainFoodSearch.items,
        equivalents,
      },
      {
        language,
        t,
        searchText: ingredientSearchText,
        filterKey: ingredientPickerFilterKey,
        sortField: selectedSortOption?.field,
        limit: 8,
      },
    );
  }, [
    equivalents,
    ingredientPickerFilterKey,
    ingredientSearchText,
    language,
    mainFoodSearch.items,
    selectedSortOption?.field,
    t,
  ]);

  const updateFormField = useCallback((key, value) => {
    applyFormUpdate((current) => ({
      ...current,
      [key]: value,
    }));
  }, [applyFormUpdate]);

  const updateSelectedIngredientQuantity = useCallback((ingredientId, value) => {
    applyFormUpdate((current) => ({
      ...current,
      ingredients: current.ingredients.map((ingredient) =>
        ingredient.id === ingredientId
          ? syncIngredientAutoCalculatedEquivalences(
              updateIngredientQuantityState(ingredient, value),
            )
          : ingredient,
      ),
    }));
  }, [applyFormUpdate]);

  const changeSelectedIngredientUnit = useCallback((ingredientId, nextUnitKey) => {
    applyFormUpdate((current) => ({
      ...current,
      ingredients: current.ingredients.map((ingredient) => {
        if (ingredient.id !== ingredientId) {
          return ingredient;
        }

        return syncIngredientAutoCalculatedEquivalences(
          updateIngredientUnitState(ingredient, nextUnitKey),
        );
      }),
    }));
  }, [applyFormUpdate]);

  const updateSelectedEquivalenceQuantity = useCallback(
    (ingredientId, equivalenceId, value) => {
      applyFormUpdate((current) => ({
        ...current,
        ingredients: current.ingredients.map((ingredient) =>
          ingredient.id === ingredientId
            ? {
                ...ingredient,
                equivalences: (ingredient.equivalences || []).map((equivalence) =>
                  equivalence.id === equivalenceId
                    ? updateIngredientQuantityState(equivalence, value)
                    : equivalence,
                ),
              }
            : ingredient,
        ),
      }));
    },
    [applyFormUpdate],
  );

  const changeSelectedEquivalenceUnit = useCallback(
    (ingredientId, equivalenceId, nextUnitKey) => {
      applyFormUpdate((current) => ({
        ...current,
        ingredients: current.ingredients.map((ingredient) => {
          if (ingredient.id !== ingredientId) {
            return ingredient;
          }

          return {
            ...ingredient,
            equivalences: (ingredient.equivalences || []).map((equivalence) => {
              if (equivalence.id !== equivalenceId) {
                return equivalence;
              }

              return applyAutoCalculatedEquivalentState(
                equivalence,
                getIngredientTotals(ingredient).calories,
                nextUnitKey,
              );
            }),
          };
        }),
      }));
    },
    [applyFormUpdate],
  );

  const removeIngredient = useCallback((ingredientId) => {
    applyFormUpdate((current) => ({
      ...current,
      ingredients: current.ingredients.filter(
        (ingredient) => ingredient.id !== ingredientId,
      ),
    }));
    setEquivalenceTargetId((currentTargetId) =>
      currentTargetId === ingredientId ? null : currentTargetId,
    );
  }, [applyFormUpdate]);

  const sensors = useNutritionDragSensors();

  const handleDragStart = useCallback(() => {
    if (!equivalenceTargetId) {
      return;
    }

    setEquivalenceTargetId(null);
    setIsIngredientBrowserOpen(false);
  }, [equivalenceTargetId]);

  const handleDragEnd = useCallback((event) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      applyFormUpdate((current) => {
        const oldIndex = current.ingredients.findIndex(
          (ingredient) => ingredient.id === active.id,
        );
        const newIndex = current.ingredients.findIndex(
          (ingredient) => ingredient.id === over.id,
        );

        return {
          ...current,
          ingredients: arrayMove(current.ingredients, oldIndex, newIndex),
        };
      });
    }
  }, [applyFormUpdate]);

  const removeEquivalence = useCallback((ingredientId, equivalenceId) => {
    applyFormUpdate((current) => ({
      ...current,
      ingredients: current.ingredients.map((ingredient) =>
        ingredient.id === ingredientId
          ? {
              ...ingredient,
              equivalences: (ingredient.equivalences || []).filter(
                (equivalence) => equivalence.id !== equivalenceId,
              ),
            }
            : ingredient,
      ),
    }));
  }, [applyFormUpdate]);

  const addIngredient = useCallback(
    (food) => {
      const isAddingEquivalence = Boolean(equivalenceTargetId);
      const currentForm = recipeEntity?.getDraftSnapshot?.() ?? form;
      const nextForm = addIngredientToRecipeForm(
        currentForm,
        (() => {
          const ingredientToAdd = buildIngredientFromSelectedFood(food, language, t);

          if (!equivalenceTargetId) {
            return ingredientToAdd;
          }

          const referenceIngredient = currentForm.ingredients.find(
            (ingredient) => ingredient.id === equivalenceTargetId,
          );

          return referenceIngredient
            ? buildLinkedEquivalentFromReference(referenceIngredient, ingredientToAdd)
            : ingredientToAdd;
        })(),
        equivalenceTargetId,
      );
      const limitsValidation = validateRecipeDraftLimits(nextForm);
      if (!limitsValidation.ok) {
        if (shouldShowNutritionLimitWarning(limitsValidation)) {
          const descriptor = getNutritionLimitMessageDescriptor(limitsValidation);
          showToast(descriptor?.title ?? "nutritionLimitReachedTitle-string", {
            type: "warning",
            description:
              descriptor?.description ?? "nutritionLimitReachedDescription-string",
            variables: getNutritionLimitMessageVariables(limitsValidation),
          });
        }
        return;
      }

      applyFormUpdate(nextForm);

      setTimeout(() => {
        focusIngredientSearchInput({
          selectText: true,
          ignorePressGuard: true,
        });
      }, 0);

      if (!isAddingEquivalence) {
        setEquivalenceTargetId(null);
      }
    },
    [
      applyFormUpdate,
      equivalenceTargetId,
      focusIngredientSearchInput,
      language,
      recipeEntity,
      form,
      showToast,
      t,
    ],
  );
  const addPickerIngredient = useCallback(
    (pickerItem) => {
      if (pickerItem?.entityKind === "equivalent") {
        const currentForm = recipeEntity?.getDraftSnapshot?.() ?? form;
        const nextForm = addEquivalentToRecipeForm(currentForm, pickerItem.sourceRecord);
        const limitsValidation = validateRecipeDraftLimits(nextForm);
        if (!limitsValidation.ok) {
          if (shouldShowNutritionLimitWarning(limitsValidation)) {
            const descriptor = getNutritionLimitMessageDescriptor(limitsValidation);
            showToast(descriptor?.title ?? "nutritionLimitReachedTitle-string", {
              type: "warning",
              description:
                descriptor?.description ?? "nutritionLimitReachedDescription-string",
              variables: getNutritionLimitMessageVariables(limitsValidation),
            });
          }
          return;
        }

        applyFormUpdate(nextForm);

        setTimeout(() => {
          focusIngredientSearchInput({
            selectText: true,
            ignorePressGuard: true,
          });
        }, 0);
        return;
      }

      addIngredient(pickerItem?.sourceRecord ?? pickerItem);
    },
    [
      addIngredient,
      applyFormUpdate,
      focusIngredientSearchInput,
      form,
      recipeEntity,
      showToast,
    ],
  );

  const openMainIngredientBrowser = useCallback(() => {
    setIngredientSearchText("");
    setIsIngredientBrowserOpen(true);
  }, []);

  const closeIngredientBrowser = useCallback(() => {
    setIngredientSearchText("");

    if (equivalenceTargetId) {
      setEquivalenceTargetId(null);
      return;
    }

    setIsIngredientBrowserOpen(false);
  }, [equivalenceTargetId]);

  const handleFoodCreated = useCallback(
    (savedFood) => {
      setIsAddFoodModalVisible(false);
      setIngredientSearchText("");
      addIngredient(savedFood);
    },
    [addIngredient],
  );

  const handleRetrySave = useCallback(() => {
    recipeEntity.saveNow();
  }, [recipeEntity]);

  const syncNotice = useMemo(() => {
    if (
      recipeEntity.lastError &&
      recipeEntity.syncStatus !== NutritionEntitySyncStatus.OFFLINE_PENDING
    ) {
      return {
        tone:
          recipeEntity.syncStatus === NutritionEntitySyncStatus.ERROR
            ? "error"
            : "warning",
        title: t("autosaveError-string"),
        message: t(
          "nutritionEditorRetryNotice-string",
          "We couldn't save the latest changes. Try saving again.",
        ),
        actionLabel: t("save-string"),
        onAction: handleRetrySave,
      };
    }

    return null;
  }, [
    handleRetrySave,
    recipeEntity.lastError,
    recipeEntity.syncStatus,
    t,
  ]);

  const handleBack = useCallback(() => {
    onBack?.();
  }, [onBack]);
  const handleEditorAction = useCallback(
    (actionKey) => {
      if (actionKey === "duplicate") {
        onDuplicate?.();
        return;
      }

      if (actionKey === "delete") {
        setIsDeleteAlertVisible(true);
      }
    },
    [onDuplicate],
  );
  const handleConfirmDelete = useCallback(async () => {
    if (typeof onDelete !== "function") {
      return;
    }

    try {
      await onDelete(recipeEntity);
    } catch (_error) {
      // Keep the alert open. The parent handler owns user-facing error feedback.
    }
  }, [onDelete, recipeEntity]);
  const deleteRecipeName = getNutritionEntityDisplayName(
    "recipe",
    recipeEntity,
    language,
  );
  const deleteAlertTitle = `${t("delete-string", "Delete").toUpperCase()} ${t(
    "recipe-string",
    "recipe",
  ).toUpperCase()}`;
  const deleteAlertText = `This will permanently delete "${deleteRecipeName}". This action cannot be undone.`;

  if (!recipeEntity) {
    return null;
  }

  return (
    <ScrollView
      style={styles.container}
      contentContainerStyle={styles.contentContainer}
      keyboardShouldPersistTaps="handled"
      showsVerticalScrollIndicator={false}
    >
      <View style={styles.topBar}>
        <TouchableOpacity
          activeOpacity={0.8}
          onPress={handleBack}
          onMouseEnter={!isMobile ? () => setIsBackButtonHovered(true) : undefined}
          onMouseLeave={!isMobile ? () => setIsBackButtonHovered(false) : undefined}
          style={[
            styles.backButton,
            isBackButtonHovered ? styles.backButtonHover : null,
          ]}
        >
          <Ionicons name="arrow-back" size={15} color={Colors.bubbleColor} />
          <Text style={styles.backButtonText}>{t("back-string")}</Text>
        </TouchableOpacity>
        <View style={styles.topBarActions}>
          <NutritionDraftStatusPill session={recipeEntity} />
          <NutritionEditorActionsMenu
            items={topBarMenuItems}
            onAction={handleEditorAction}
          />
        </View>
      </View>

      <NutritionSyncNotice
        session={recipeEntity}
        tone={syncNotice?.tone}
        title={syncNotice?.title}
        message={syncNotice?.message}
        actionLabel={syncNotice?.actionLabel}
        onAction={syncNotice?.onAction}
        onRetry={handleRetrySave}
        style={styles.syncNotice}
      />

      <View
        style={[
          styles.editorColumns,
          isCompactLayout ? styles.editorColumnsCompact : null,
        ]}
      >
        <View
          style={[
            styles.editorMainColumn,
            isCompactLayout ? styles.editorMainColumnCompact : null,
          ]}
        >
          <SectionCard
            style={isCompactLayout ? styles.sectionCardCompact : null}
            icon="restaurant-outline"
            title={isEditing ? t("editRecipe-string") : t("createRecipe-string")}
          >
            <RecipeEditorDetailsSection
              t={t}
              form={form}
              updateFormField={updateFormField}
              recipeClassification={recipeClassification}
            />
            <RecipeIngredientsSection
              t={t}
              form={form}
              saveValidationMessage={saveValidationMessage}
              sensors={sensors}
              handleDragStart={handleDragStart}
              handleDragEnd={handleDragEnd}
              useStackedIngredientLayout={useStackedIngredientLayout}
              getIngredientTotals={getIngredientTotals}
              equivalenceTargetId={equivalenceTargetId}
              setEquivalenceTargetId={setEquivalenceTargetId}
              updateSelectedIngredientQuantity={updateSelectedIngredientQuantity}
              changeSelectedIngredientUnit={changeSelectedIngredientUnit}
              updateSelectedEquivalenceQuantity={
                updateSelectedEquivalenceQuantity
              }
              changeSelectedEquivalenceUnit={changeSelectedEquivalenceUnit}
              removeIngredient={removeIngredient}
              removeEquivalence={removeEquivalence}
              searchInputRef={searchInputRef}
              ingredientSearchInputPressingRef={ingredientSearchInputPressingRef}
              focusIngredientSearchInput={focusIngredientSearchInput}
              ingredientSearchText={ingredientSearchText}
              setIngredientSearchText={setIngredientSearchText}
              ingredientSortOptions={ingredientSortOptions}
              selectedSortOption={selectedSortOption}
              ingredientSortKey={ingredientSortKey}
              setIngredientSortKey={setIngredientSortKey}
              mainFilterOptions={mainFilterOptions}
              selectedMainFilterOption={selectedMainFilterOption}
              mainFilterKey={ingredientPickerFilterKey}
              setMainFilterKey={setIngredientPickerFilterKey}
              mainIngredientResults={mainIngredientResults}
              mainIngredientIsLoading={mainFoodSearch.isLoading}
              mainIngredientLoadingSessionId={mainFoodSearch.loadingSessionId}
              inlineFilterOptions={inlineFilterOptions}
              selectedInlineFilterOption={selectedInlineFilterOption}
              inlineFilterKey={equivalencePickerDatabaseKey}
              setInlineFilterKey={setEquivalencePickerDatabaseKey}
              inlineIngredientResults={ingredientResults}
              inlineIngredientIsLoading={inlineFoodSearch.isLoading}
              inlineIngredientLoadingSessionId={inlineFoodSearch.loadingSessionId}
              language={language}
              addPickerIngredient={addPickerIngredient}
              addInlineEquivalentIngredient={addIngredient}
              onCreateFood={() => setIsAddFoodModalVisible(true)}
              equivalenceTargetIngredient={equivalenceTargetIngredient}
              closeIngredientBrowser={closeIngredientBrowser}
              isIngredientBrowserOpen={isIngredientBrowserOpen}
              openMainIngredientBrowser={openMainIngredientBrowser}
              mainPickerPanelRootRef={mainPickerPanelRootRef}
              equivalencePickerPanelRootRef={equivalencePickerPanelRootRef}
            />
          </SectionCard>
        </View>

        <View
          style={[
            styles.editorSummaryColumn,
            isCompactLayout ? styles.editorSummaryColumnCompact : null,
          ]}
        >
          <SectionCard
            style={isCompactLayout ? styles.sectionCardCompact : null}
            icon="analytics-outline"
            title={t("summary-string")}
          >
            <RecipeEditorSummarySection
              t={t}
              isMobile={isMobile}
              recipeTotals={recipeTotals}
              macroBreakdown={macroBreakdown}
              macronutrientsTitle={macronutrientsTitle}
              summaryMacroItems={summaryMacroItems}
              micronutrientSummaryItems={micronutrientSummaryItems}
              microsExpanded={microsExpanded}
              setMicrosExpanded={setMicrosExpanded}
            />
          </SectionCard>
        </View>
      </View>

      <CustomAlert
        isVisible={isDeleteAlertVisible}
        onClose={() => setIsDeleteAlertVisible(false)}
        title={deleteAlertTitle}
        text={deleteAlertText}
        noTranslateText={true}
        action={handleConfirmDelete}
        specialCTA="delete-string"
      />
      <AddFoodModal
        isVisible={isAddFoodModalVisible}
        onClose={() => setIsAddFoodModalVisible(false)}
        onSaved={handleFoodCreated}
        coach={coach}
        food={null}
      />
    </ScrollView>
  );
});

const styles = StyleSheet.create({
  container: {
    flex: 1,
    width: "100%",
    marginTop: 24,
  },
  contentContainer: {
    paddingBottom: 36,
  },
  editorColumns: {
    flexDirection: "row",
    alignItems: "flex-start",
    gap: 16,
  },
  editorColumnsCompact: {
    flexDirection: "column",
    alignItems: "stretch",
    gap: 12,
  },
  editorMainColumn: {
    flex: 1,
    minWidth: 0,
  },
  editorMainColumnCompact: {
    flexGrow: 0,
    flexShrink: 0,
    flexBasis: "auto",
    width: "100%",
    alignSelf: "stretch",
  },
  editorSummaryColumn: {
    width: 360,
    maxWidth: "40%",
  },
  editorSummaryColumnCompact: {
    flexGrow: 0,
    flexShrink: 0,
    flexBasis: "auto",
    width: "100%",
    maxWidth: "100%",
    alignSelf: "stretch",
  },
  topBar: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
    marginBottom: 12,
  },
  backButton: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 10,
    transition: "opacity 180ms ease-in-out",
  },
  backButtonHover: {
    opacity: 0.8,
  },
  backButtonText: {
    fontSize: 13,
    fontWeight: "500",
    color: Colors.bubbleColor,
  },
  topBarActions: {
    flexDirection: "row",
    alignItems: "center",
    gap: 10,
  },
  syncNotice: {
    marginBottom: 18,
  },
  secondaryButton: {
    paddingHorizontal: 14,
    paddingVertical: 8,
    borderRadius: 10,
    borderWidth: 0.5,
    borderColor: Colors.borderColor,
    backgroundColor: Colors.whiteColor,
  },
  secondaryButtonHovered: {
    backgroundColor: Colors.backgroundColor,
  },
  secondaryButtonText: {
    fontSize: 13,
    fontWeight: "500",
    color: Colors.darkgrayColor,
  },
  primaryButton: {
    paddingHorizontal: 14,
    paddingVertical: 8,
    borderRadius: 10,
    backgroundColor: Colors.bubbleColor,
  },
  primaryButtonHovered: {
    opacity: 0.9,
  },
  primaryButtonDisabled: {
    opacity: 0.45,
  },
  primaryButtonText: {
    ...Fonts.medium13,
    color: Colors.whiteColor,
  },
  sectionCard: {
    backgroundColor: Colors.whiteColor,
    borderRadius: 14,
    borderWidth: 0.5,
    borderColor: Colors.borderColor,
    padding: 18,
    marginBottom: 16,
  },
  sectionCardCompact: {
    marginBottom: 0,
  },
  sectionTitleUppercase: {
    textTransform: "uppercase",
  },
  sectionIconWrap: {
    width: 30,
    height: 30,
    alignItems: "center",
    justifyContent: "center",
  },
});
